/**
 * Signing in to this server, or making an account on it: with Google or GitHub when the server
 * offers them (one click, no password), or an email and a password. An account with the second
 * step on is then asked the code of its authenticator app (or a recovery code). Afterwards the
 * page goes back to the space it was turned away from (`?next=`), or shows your teams.
 */
import { For, Show, createResource, createSignal, type JSX } from "solid-js";
import { accountsApi, signInWithUrl } from "../client/accounts";
import { usePages } from "./context";
import { nextPath, withoutReturns } from "./routes";

/** The sign-in card; `creating` starts on "make an account" (an invite's newcomers). */
export function SignInCard(props: { creating?: boolean }): JSX.Element {
  const { accounts, server } = usePages();
  const [creating, setCreating] = createSignal(props.creating ?? false);
  const [email, setEmail] = createSignal("");
  const [name, setName] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [code, setCode] = createSignal("");
  /** The sign-in on this server waiting for its code, if one is. */
  const waiting = () => (accounts.codeNeeded()?.server === server ? accounts.codeNeeded() : null);

  /** Signs in or up (with the code once asked); then back to the space that asked, if any. */
  const submit = async () => {
    // Read first: once signed in, the dashboard shows a team and rewrites the address.
    const next = nextPath(location.search);
    const params = new URLSearchParams(location.search);
    params.delete("next");
    setBusy(true);
    const asked = waiting();
    const done = asked?.ticket
      ? await accounts.finishSignIn(server, asked.ticket, code())
      : asked
        ? await accounts.signIn(server, email(), password(), code())
        : creating()
          ? await accounts.signUp(server, email(), name(), password())
          : await accounts.signIn(server, email(), password());
    setBusy(false);
    if (!done) return;
    setPassword("");
    setCode("");
    // Back where the page was turned away, with its other parameters.
    if (next) location.assign(`${next}${params.size ? `?${params}` : ""}`);
  };

  // The services this server signs in with (none: an older server, or passwords only).
  const [providers] = createResource(() => accountsApi(server).providers().catch(() => []));
  /** Where the service sends the browser back: this page, as it is now (an invite, `?next=`). */
  const back = () => `${location.pathname}${withoutReturns(location.search)}`;

  return (
    <Show when={!waiting()} fallback={<CodeCard code={code()} setCode={setCode} busy={busy()} submit={submit} cancel={() => accounts.cancelCode()} />}>
    <section class="pages-card signin" id="signin" data-mode={creating() ? "create" : "signin"}>
      <h2>{creating() ? "Make your account" : "Sign in"}</h2>
      <p class="muted">{creating() ? "One account for every team you are in on this server." : "Your teams and their spaces are waiting."}</p>
      <Show when={(providers() ?? []).length > 0}>
        <div class="signin-providers" id="signin-providers">
          <For each={providers()}>
            {(provider) => (
              <a class="btn provider" data-provider={provider.id} href={signInWithUrl(server, provider.id, back())}>
                <ProviderIcon id={provider.id} />
                Continue with {provider.name}
              </a>
            )}
          </For>
        </div>
        <p class="signin-or small muted">or with your email</p>
      </Show>
      <form
        class="pages-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label>
          Email
          <input class="field" id="signin-email" type="email" autocomplete="username" required value={email()} onInput={(e) => setEmail(e.currentTarget.value)} />
        </label>
        <Show when={creating()}>
          <label>
            Name <span class="muted small">(shown to your teams)</span>
            <input class="field" id="signin-name" autocomplete="name" required value={name()} onInput={(e) => setName(e.currentTarget.value)} />
          </label>
        </Show>
        <label>
          Password <Show when={creating()}><span class="muted small">(at least 8 characters)</span></Show>
          <input
            class="field"
            id="signin-password"
            type="password"
            autocomplete={creating() ? "new-password" : "current-password"}
            required
            value={password()}
            onInput={(e) => setPassword(e.currentTarget.value)}
          />
        </label>
        <button type="submit" class="btn primary" id="signin-submit" disabled={busy()}>
          {creating() ? "Create account" : "Sign in"}
        </button>
      </form>
      <p class="small">
        {creating() ? "Already have an account? " : "New here? "}
        <button type="button" class="linklike" id="signin-mode" onClick={() => setCreating(!creating())}>
          {creating() ? "Sign in" : "Create an account"}
        </button>
      </p>
    </section>
    </Show>
  );
}

/** The second step: the code the authenticator app shows now, or a recovery code. */
function CodeCard(props: { code: string; setCode: (code: string) => void; busy: boolean; submit: () => Promise<void>; cancel: () => void }): JSX.Element {
  return (
    <section class="pages-card signin" id="signin-second-step">
      <h2>Two-step sign-in</h2>
      <p class="muted">Type the 6-digit code your authenticator app shows for Knodal. Lost your phone? Use one of your recovery codes.</p>
      <form
        class="pages-form"
        onSubmit={(e) => {
          e.preventDefault();
          void props.submit();
        }}
      >
        <label>
          Code
          <input
            class="field mono"
            id="signin-code"
            inputmode="numeric"
            autocomplete="one-time-code"
            required
            autofocus
            value={props.code}
            onInput={(e) => props.setCode(e.currentTarget.value)}
          />
        </label>
        <button type="submit" class="btn primary" id="signin-code-submit" disabled={props.busy}>
          Sign in
        </button>
      </form>
      <p class="small">
        <button type="button" class="linklike" id="signin-code-cancel" onClick={props.cancel}>
          Back
        </button>
      </p>
    </section>
  );
}

/** The mark of a sign-in service (`google`, `github`), drawn in its usual colours. */
function ProviderIcon(props: { id: string }): JSX.Element {
  return (
    <Show
      when={props.id === "google"}
      fallback={
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          <path d="M12 .5a11.5 11.5 0 0 0-3.64 22.41c.58.1.79-.25.79-.56v-2c-3.2.7-3.88-1.37-3.88-1.37-.53-1.33-1.28-1.69-1.28-1.69-1.05-.71.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.77 2.7 1.26 3.36.96.1-.75.4-1.26.73-1.55-2.55-.29-5.24-1.28-5.24-5.68 0-1.26.45-2.28 1.19-3.09-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.17 1.18a11 11 0 0 1 5.77 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.23 2.76.11 3.05.74.81 1.19 1.83 1.19 3.09 0 4.41-2.69 5.38-5.26 5.67.41.36.78 1.06.78 2.14v3.17c0 .31.21.67.8.56A11.5 11.5 0 0 0 12 .5Z" />
        </svg>
      }
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.17 3.57-8.81Z" />
        <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.95-2.92l-3.87-3c-1.08.72-2.45 1.15-4.08 1.15-3.13 0-5.79-2.12-6.74-4.97h-4v3.09A12 12 0 0 0 12 24Z" />
        <path fill="#FBBC05" d="M5.26 14.26a7.2 7.2 0 0 1 0-4.52V6.65h-4a12 12 0 0 0 0 10.7l4-3.09Z" />
        <path fill="#EA4335" d="M12 4.77c1.76 0 3.35.61 4.6 1.8l3.43-3.43A11.5 11.5 0 0 0 12 0 12 12 0 0 0 1.26 6.65l4 3.09C6.21 6.89 8.87 4.77 12 4.77Z" />
      </svg>
    </Show>
  );
}
